import classNames from 'classnames';
import Label from 'components/Label';
import { shouldHideOption } from 'helpers/shouldHideOption';
import { RichText } from 'prismic-reactjs';
import PropTypes from 'prop-types';
import { prop, uniqBy } from 'ramda';
import { JsonParam, useQueryParam } from 'use-query-params';
import { usePcConfigurator } from 'utils/PcConfiguratorContext';
import PcModelOptionTagDebugger from '../PcModelOptionTagDebugger';
import styles from './styles';

const PcModelOptionsGroupList = ({ groupType, subGroup, options }) => {
  const { attrTags, sortingOptions } = usePcConfigurator();

  const baseOption = options.find(
    (option) => option.group_type === groupType && option.is_base,
  );

  const [selections, setSeletions] = useQueryParam('selections', JsonParam);

  const currentOption = options.find((option) => {
    const variantId = selections?.[groupType] || baseOption.variant_id;
    return option.variant_id === variantId;
  });

  const [debugMode] = useQueryParam('debug');

  const subGroupOptions = uniqBy(
    prop('id'),
    options.filter(
      (option) =>
        !subGroup ||
        option.option_sub_group_id === subGroup.option_sub_group_id,
    ),
  ).sort(sortingOptions.sortInSubGroupOptions);

  const isHidden = (option) => shouldHideOption(attrTags, option);

  const subGroupDescription = subGroup?.sub_group_description
    ? RichText.asText(subGroup?.sub_group_description)
    : '';

  return (
    <>
      {subGroup?.sub_group_title && (
        <div className="sub-group-list-title">{subGroup?.sub_group_title}</div>
      )}

      {subGroupDescription && (
        <div className="sub-group-list-description">
          {subGroup?.sub_group_description[0]?.text}
        </div>
      )}

      <div className="sub-group-lists-options">
        {subGroupOptions.map((option, index) => (
          <div
            key={`${option.variant_id}-${index}`}
            className={classNames('sub-group-list-options', {
              hidden: isHidden(option),
            })}
          >
            <input
              type="radio"
              value={option.variant_id}
              id={`${option.group_type}-option-${option.variant_id}`}
              name={`${option.group_type}-option`}
              onChange={() => {
                if (option.variant_id) {
                  setSeletions({
                    ...selections,
                    [option.group_type]: option.variant_id,
                  });
                } else {
                  const currentSelections = { ...selections };
                  delete currentSelections[option.group_type];
                  setSeletions(currentSelections);
                }
              }}
              checked={option?.variant_id === currentOption?.variant_id}
              disabled={!option?.in_stock && !option?.is_base}
            />
            <label htmlFor={`${option.group_type}-option-${option.variant_id}`}>
              <span
                className={classNames('radio-label', {
                  disabled_text: !option.in_stock,
                })}
              >
                {option.title || '- None -'}
              </span>
              {option.compared_at_price_diff > 0 && (
                <span className="compared_at_price_diff">
                  <span> &#36;</span>
                  {option.compared_at_price_diff}
                </span>
              )}
              {!!option.price_diff && (
                <span className={option.price_diff < 0 ? 'red' : 'green'}>
                  <span>{option.price_diff < 0 ? '-' : '+'}</span>
                  <span>&#36;</span>
                  {Math.abs(option.price_diff)}
                </span>
              )}

              {option.is_base && <Label type="blue" text="Base" />}

              {option.is_recommended && <Label text="Recommended" />}

              {!option.in_stock && <Label type="grey" text="Out of Stock" />}

              {((!option.in_stock && option.is_base) ||
                (selections?.[groupType] === option.variant_id &&
                  !option.in_stock)) && <Label type="yellow" text="Delayed" />}
            </label>

            {debugMode && (
              <PcModelOptionTagDebugger
                variant_id={option?.variant_id}
                tags={option.tags}
              />
            )}
          </div>
        ))}
      </div>
      <style jsx>{styles} </style>
    </>
  );
};

PcModelOptionsGroupList.propTypes = {
  groupType: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.object).isRequired,
  subGroup: PropTypes.object,
};

PcModelOptionsGroupList.defaultProps = {
  subGroup: undefined,
};

export default PcModelOptionsGroupList;
